// Вкладка «Состав тела» (/health/inbody): «Загрузить отчёт», «Ждут проверки», карточки последнего
// замера, график с коридором нормы, фигура с сегментами, сравнение, таблица замеров, инструкция для iPhone.

import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Button, Card, EmptyState, ErrorText, IconButton, Spinner, useToast } from '@/components/ui'
import { formatDate, formatDateTime } from '@/lib/format'

import { inbodyApi, useDrafts, useInBodySummary, useInvalidateInBody, useScans } from './api'
import { IPhoneHelp } from './IPhoneHelp'
import { ChartCard } from './overview/ChartCard'
import { ComparisonCard } from './overview/ComparisonCard'
import { FigureCard } from './overview/FigureCard'
import { ScansTable } from './overview/ScansTable'
import { StatCards } from './overview/StatCards'
import { TargetsSection } from './targets/TargetsSection'

const IMPORT_PATH = '/health/inbody/import'

function Drafts() {
  const toast = useToast()
  const invalidate = useInvalidateInBody()
  const drafts = useDrafts()
  const discard = useMutation({
    mutationFn: inbodyApi.discardDraft,
    networkMode: 'always',
    onSuccess: () => {
      void invalidate()
      toast.show({ message: 'Черновик удалён' })
    },
    onError: (error) => toast.show({ message: error instanceof ApiError ? error.message : 'Не удалось удалить', tone: 'danger' }),
  })
  const items = drafts.data ?? []
  if (items.length === 0) return null
  return (
    <Card title="Ждут проверки" subtitle="Разобранные отчёты, которые ещё не сохранены. Через 30 дней удаляются сами.">
      <ul className="divide-y divide-border">
        {items.map((d) => (
          <li key={d.id} className="flex min-h-12 items-center gap-3 py-1.5">
            <Link to={`${IMPORT_PATH}?draft=${d.id}`} className="min-w-0 flex-1 hover:underline">
              <div className="text-sm font-semibold text-fg">Замер за {formatDate(d.parsed.values.day)}</div>
              <div className="truncate text-xs text-muted">
                {d.filename ?? 'PDF'} · загружен {formatDateTime(d.uploaded_at)}
              </div>
            </Link>
            <IconButton
              icon="close"
              label={`Удалить черновик за ${formatDate(d.parsed.values.day)}`}
              onClick={() => discard.mutate(d.id)}
              disabled={discard.isPending}
            />
          </li>
        ))}
      </ul>
    </Card>
  )
}

function Overview() {
  const summary = useInBodySummary()
  const scans = useScans()
  if (summary.isLoading || scans.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    )
  }
  const error = summary.error ?? scans.error
  if (error) return <ErrorText error={error} />
  const items = scans.data ?? []
  const data = summary.data
  const latest = data?.latest ?? null
  if (items.length === 0 && !latest) {
    return (
      <Card>
        <EmptyState
          icon="target"
          title="Пока нет замеров InBody"
          description="Загрузи PDF-отчёт из DDX или поделись им из приложения DDX на Android — цифры разберутся сами."
        />
      </Card>
    )
  }
  return (
    <>
      {data && latest ? <StatCards summary={data} /> : null}
      {data ? <FigureCard summary={data} /> : null}
      <ChartCard latest={latest} />
      {data && latest ? <ComparisonCard summary={data} /> : null}
      {data ? <TargetsSection summary={data} /> : null}
      <ScansTable scans={items} />
    </>
  )
}

export function InBodyPage() {
  const navigate = useNavigate()
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" icon="plus" onClick={() => navigate(IMPORT_PATH)}>
          Загрузить отчёт
        </Button>
        <Button onClick={() => navigate(`${IMPORT_PATH}?manual=1`)}>Ввести вручную</Button>
      </div>
      <Drafts />
      <Overview />
      <IPhoneHelp />
    </div>
  )
}
